import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { t } from 'i18next';

import { Box, Chip } from '@mui/material';

import TableComponent, { IColumnProps } from '../../components/TableComponent';
import ImageModalComponent from '../../components/ImageModalComponent';
import ImageSimpleColumnComponent from '../../components/ImageSimpleColumnComponent';
import PauseButtonComponent from '../../components/PauseButtonComponent';
import PanicAlarmCentralPopupComponent from 'components/PanicAlarmCentralPopupComponent';

import LastSchoolEvent from './_components/LastSchoolEvent';
import VideoModalComponent from './_components/VideoModalComponent';
import { ClassCell } from './_components/ClassCell';
import { RecordingActionCell } from './_components/RecordingActionCell';

import { AppContext } from '../../context/AppContextProvider';
import { getEventTargetData } from 'services/Events/SchoolMonitoringEventService/utils';
import { useSchoolMonitoringEventDataStreamCache } from '../../services/Events/SchoolMonitoringEventService/hooks/useQueryClient';
import {
  SCHOOL_MONITORING_KEYS,
  useGetAllSchoolMonitoringEventByLicenseQuery,
  useGetAllSchoolMonitoringEventCountByLicenseQuery,
} from '../../services/Events/SchoolMonitoringEventService/hooks/useQuery';
import { useSchoolMonitoringSocket } from '../../services/Events/SchoolMonitoringEventService/hooks/useSocket';
import { useDebounce } from '../../services/Events/SchoolMonitoringEventService/hooks/useDebounce';

import { IEventPauseUpdateParams } from '../../services/Events/types';
import { IPaginationParams } from '../../services/types';

const SchoolMonitoringPage = () => {
  const { setIsShowLoadingComponent } = useContext(AppContext);
  const queryClient = useQueryClient();
  const { setCacheData, clearCacheData } = useSchoolMonitoringEventDataStreamCache();

  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [modalState, setModalState] = useState({ isOpen: false, url: '' });
  const [videoModal, setVideoModal] = useState({ isOpen: false, url: '' });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const filter = useMemo(() => ({
      search: debouncedSearchTerm
  }), [debouncedSearchTerm]);

  const { 
    data: eventsPage = [], 
    isLoading: isLoadingList 
  } = useGetAllSchoolMonitoringEventByLicenseQuery(pagination, filter);

  const { 
    data: totalCount = 0, 
    isLoading: isLoadingCount 
  } = useGetAllSchoolMonitoringEventCountByLicenseQuery(filter);

  const [pausedUpdateParams, setPausedUpdateParams] =
    useState<IEventPauseUpdateParams>({
      isPaused: false,
      cacheKey: SCHOOL_MONITORING_KEYS.LIST,
      setCacheData: (data: any[], pagination: IPaginationParams) => data,
      getCacheData: () => {
         const currentCache = queryClient.getQueryData([
          SCHOOL_MONITORING_KEYS.ALL, 
          SCHOOL_MONITORING_KEYS.LIST, 
          pagination, 
          filter
        ]);
         return (currentCache as any[]) || [];
      },
      clearCacheData: () => {},
    });

  useSchoolMonitoringSocket({
    pagination,
    filter, 
    pausedUpdateParams
  });

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageNumber: 1 }));
  }, [debouncedSearchTerm]);

  useEffect(() => {
     if(isLoadingList || isLoadingCount) {
        setIsShowLoadingComponent(true);
     } else {
        setIsShowLoadingComponent(false);
     }
  }, [isLoadingList, isLoadingCount, setIsShowLoadingComponent]);

  const handleFilterChange = (val: string) => {
    setSearchTerm(val);
  };

  const handleOpenImageModal = (url?: string) => {
    if (!url) {
      console.warn('Tentativa de abrir imagem inexistente bloqueada.');
      return; 
    }
    
    setModalState({ isOpen: true, url });
  };

  const columns: IColumnProps[] = [
    {
      id: 'recognitionImage',
      label: t('common.image'),
      align: 'center',
      width: '0%',
      render: (url: string) => (
        <ImageSimpleColumnComponent 
          url={url} 
          onClick={() => handleOpenImageModal(url)}
        />
      ),
    },
    {
      id: 'name',
      label: t('common.name'),
      width: '30%',
      render: (_, idx) => <>{getEventTargetData(eventsPage[idx]).name}</>,
    },
    {
        id: 'className',
        label: t('routersNames.class'),
        width: '25%',
        render: (_, idx) => <ClassCell event={eventsPage[idx]} />,
    },
    { 
      id: 'device.name', 
      label: t('common.device'), 
      width: '10%' 
    },
    {
      id: 'schoolMonitoringType',
      label: t('common.type'),
      align: 'center',
      width: '5%',
      render: (_, idx) => {
        const { typeLabel, color } = getEventTargetData(eventsPage[idx]);
        return <Chip label={t(typeLabel)} color={color} />;
      },
    },
    {
      id: 'faceImage',
      label: t('common.face'),
      align: 'center',
      width: '0%',
      render: (_, idx) => {
        const { image } = getEventTargetData(eventsPage[idx]);
        return (
          <ImageSimpleColumnComponent 
            url={image} 
            onClick={() => handleOpenImageModal(image)}
          />
        );
      },
    },
    // {
    //   id: 'recordingAction',
    //   label: t('common.recording'),
    //   align: 'center',
    //   width: '0%',
    //   render: (_, idx) =>
    //     <RecordingActionCell
    //       event={eventsPage[idx]}
    //       onPlayVideo={(url) => setVideoModal({ isOpen: true, url: url })}
    //     />,
    // },
    {
      id: 'createdAt',
      label: t('common.date'),
      width: '0%',
      render: (val) => <>{new Date(val).toLocaleString('pt-BR')}</>,
    },
  ]

  return (
    <>
      <Box mb={3}>
        {pagination.pageNumber === 1 && eventsPage.length > 0 && eventsPage[0] && (
          <LastSchoolEvent 
            event={eventsPage[0]} 
            handleViewImage={(url) => handleOpenImageModal(url)}
          />
        )}
      </Box>
      <Box mt={1}>
        <TableComponent
          rows={eventsPage}
          columns={columns}
          totalCount={totalCount}
          currentPageNumber={pagination.pageNumber}
          onChangePageOrRowsPerPage={setPagination}
          onChangeMainFilter={handleFilterChange}
          searchValue={searchTerm}
          additionalTitleComponent={
            <PauseButtonComponent
              pausedUpdateParams={pausedUpdateParams}
              setPausedUpdateParams={setPausedUpdateParams}
            />
          }
          title={t('common.lastEvent')}
        />

        <ImageModalComponent
          isOpen={modalState.isOpen}
          handleClose={() => setModalState({ ...modalState, isOpen: false })}
          imageUrl={modalState.url}
        />

        <VideoModalComponent
          isOpen={videoModal.isOpen}
          handleClose={() => setVideoModal({ ...videoModal, isOpen: false })}
          videoUrl={videoModal.url}
        />
      </Box>
      <PanicAlarmCentralPopupComponent />
    </>
  );
};

export default SchoolMonitoringPage;
